import { useCallback, useEffect, useRef, useState } from "react";
import type { IdentifyAdmission } from "../../../src/display/types";

export type IdentifyRequestState =
  | { status: "idle" }
  | { status: "requesting" }
  | ({ status: "complete" } & IdentifyAdmission)
  | { status: "failed"; message: string };

const REQUEST_TIMEOUT_MS = 10_000;
const KEY_PATTERN = /^[A-Za-z0-9_-]{16,128}$/;

function newIdempotencyKey(): string {
  const key = crypto.randomUUID();
  if (!KEY_PATTERN.test(key)) throw new Error("Unable to create request identity");
  return key;
}

function parseAdmission(value: unknown): IdentifyAdmission | null {
  if (!value || typeof value !== "object") return null;
  const input = value as Record<string, unknown>;
  if (["busy", "offline", "unsupported"].includes(String(input.outcome))) {
    return { outcome: input.outcome as "busy" | "offline" | "unsupported" };
  }
  if (
    input.outcome === "accepted" &&
    typeof input.requestId === "string" && input.requestId.length <= 128 &&
    Number.isSafeInteger(input.generation) && (input.generation as number) >= 1 &&
    typeof input.expiresAt === "string" && Number.isFinite(Date.parse(input.expiresAt))
  ) {
    return {
      outcome: "accepted",
      requestId: input.requestId,
      generation: input.generation as number,
      expiresAt: new Date(input.expiresAt).toISOString()
    };
  }
  return null;
}

export function useIdentify(deviceId: string | undefined) {
  const [state, setState] = useState<IdentifyRequestState>({ status: "idle" });
  const active = useRef<{ controller: AbortController; deviceId: string } | null>(null);

  useEffect(() => {
    setState({ status: "idle" });
    return () => {
      active.current?.controller.abort();
      active.current = null;
    };
  }, [deviceId]);

  const identify = useCallback(async () => {
    if (!deviceId || active.current) return;
    const key = newIdempotencyKey();
    const operation = { controller: new AbortController(), deviceId };
    const { controller } = operation;
    active.current = operation;
    setState({ status: "requesting" });
    const deadline = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
    try {
      let response: Response | null = null;
      for (let attempt = 0; attempt < 2; attempt += 1) {
        try {
          response = await fetch(`/api/devices/${encodeURIComponent(deviceId)}/identify`, {
            method: "POST",
            credentials: "include",
            headers: { "Idempotency-Key": key },
            signal: controller.signal
          });
          if (response.status < 500) break;
        } catch (error) {
          if (controller.signal.aborted || attempt === 1) throw error;
        }
      }
      if (!response?.ok) {
        const body: unknown = await response?.json().catch(() => null);
        const code = (body as { error?: { code?: unknown } } | null)?.error?.code;
        throw new Error(
          code === "IDENTIFY_TIMEOUT"
            ? "Identify timed out waiting on the server; try again."
            : "Identify request failed"
        );
      }
      const admission = parseAdmission(await response.json());
      if (!admission) throw new Error("Identify response was not understood");
      if (active.current === operation && operation.deviceId === deviceId)
        setState({ status: "complete", ...admission });
    } catch (error) {
      if (active.current === operation && operation.deviceId === deviceId) {
        setState({
          status: "failed",
          message: controller.signal.aborted
            ? "Identify request timed out; phone status is unknown."
            : error instanceof Error ? error.message : "Identify request failed"
        });
      }
    } finally {
      clearTimeout(deadline);
      if (active.current === operation) active.current = null;
    }
  }, [deviceId]);

  return { state, identify };
}
